import type { Me } from "@contract/firm/me";
import * as React from "react";
import { fetchMe } from "../../api/me";

export type SignInWatch = "waiting" | "slow" | "signedIn";

/** How often /v1/me is asked whether the add-in has signed in, and when the wait reads as slow. */
const POLL_MS = 4000;
const SLOW_AFTER_MS = 120_000;

/**
 * The onboarding's last step (A8, Dan's frame 15185:5898): watches /v1/me for the add-in's
 * first sign-in (`pluginSignedIn`) while `active`, every few seconds and only while the tab
 * is visible. "slow" after two minutes of waiting. Calls `onSignedIn` once, so Home's /v1/me
 * catches up.
 */
export function useSignInWatch({
  active,
  alreadySignedIn,
  onSignedIn,
  load = fetchMe,
  pollMs = POLL_MS,
  slowAfterMs = SLOW_AFTER_MS,
}: {
  active: boolean;
  alreadySignedIn: boolean;
  onSignedIn: () => void;
  load?: () => Promise<Me>;
  pollMs?: number;
  slowAfterMs?: number;
}): SignInWatch {
  const [state, setState] = React.useState<SignInWatch>(alreadySignedIn ? "signedIn" : "waiting");
  const notified = React.useRef(false);

  React.useEffect(() => {
    if (alreadySignedIn) setState("signedIn");
  }, [alreadySignedIn]);

  React.useEffect(() => {
    if (!active || state === "signedIn") return undefined;
    let alive = true;
    const started = Date.now();
    const tick = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const me = await load();
        if (!alive) return;
        if (me.pluginSignedIn) {
          setState("signedIn");
          if (!notified.current) {
            notified.current = true;
            onSignedIn();
          }
          return;
        }
      } catch {
        // A failed check waits for the next one.
      }
      if (alive && Date.now() - started >= slowAfterMs) setState("slow");
    };
    const timer = setInterval(() => void tick(), pollMs);
    void tick();
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [active, state === "signedIn"]);

  return state;
}
